import { useMemo, useState } from "react";
import { SegmentedControl, Skeleton, cn } from "@fox1ck-org/ui";

import { DASH, formatInt, formatMoney, formatPct, formatPp } from "@/lib/format";
import { derive, type Bag } from "@/lib/campaigns-table";
import { daysIn } from "@/lib/period";
import { toBag } from "@/lib/report";
import { useT, type TKey } from "@/lib/i18n";
import type { ReportEngine } from "./engine";

type Series = "profit" | "leads" | "cr" | "clicks";
const SERIES: Series[] = ["profit", "leads", "cr", "clicks"];
const LABEL: Record<Series, TKey> = { profit: "rep.series.profit", leads: "rep.series.leads", cr: "rep.series.cr", clicks: "rep.series.clicks" };

/**
 * «Профит по дням» (доска Report): the same report grouped by day, the other
 * period as a dashed mark on each bar, today hatched while it runs.
 * Collapsed it is one line with the period's figure (доска ReportEmpty).
 */
export function DailyBand({ engine, open, onToggle }: { engine: ReportEngine; open: boolean; onToggle: () => void }) {
  const t = useT();
  const [series, setSeries] = useState<Series>("profit");
  const { schema, totals, prevTotals, state } = engine;
  const p = state.params;

  const days = useMemo(() => {
    if (!schema) return [];
    const bag = (s: Parameters<typeof toBag>[0]) => derive(schema.metrics, toBag(s) ?? {});
    const cur = [...(engine.daily.data?.rows ?? [])].sort((a, b) => (a.dimensionPath[0] ?? "").localeCompare(b.dimensionPath[0] ?? ""));
    const prev = [...(engine.dailyPrev.data?.rows ?? [])].sort((a, b) => (a.dimensionPath[0] ?? "").localeCompare(b.dimensionPath[0] ?? ""));
    const n = Math.min(daysIn({ from: p.from, to: p.to }), 14);
    return cur.slice(-n).map((r, i, all) => ({ day: r.dimensionPath[0] ?? "", v: bag(r.stats), prev: prev[prev.length - all.length + i] ? bag(prev[prev.length - all.length + i]!.stats) : undefined }));
  }, [engine.daily.data, engine.dailyPrev.data, schema, p.from, p.to]);

  const val = (v: Bag | undefined) => v?.[series];
  const fmt = (n: number | undefined, signed = false) => (series === "profit" ? formatMoney(n, { signed }) : series === "cr" ? formatPct(n) : formatInt(n));
  const max = Math.max(1, ...days.flatMap((d) => [Math.abs(val(d.v) ?? 0), Math.abs(val(d.prev) ?? 0)]));
  // No rows, no number (доска ReportEmpty: «—»): a sum over nothing is not a zero profit.
  const total = engine.tree.length ? val(totals) : undefined;
  const tone = series === "profit" && total !== undefined ? (total > 0 ? "text-ok" : total < 0 ? "text-destructive" : "") : "";

  if (!open) {
    return (
      <button type="button" onClick={onToggle} className="flex h-9 shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-4 text-left">
        <b className="text-sm font-semibold">{t(LABEL[series])}</b>
        <span className="text-xs text-muted-foreground">{t("rep.band.expand")}</span>
        <span className="flex-1" />
        <span className={cn("font-mono text-xs", tone)}>{fmt(total, true)}</span>
      </button>
    );
  }
  if (engine.daily.isLoading || !schema) return <Skeleton className="h-33 shrink-0 rounded-lg" />;

  const today = p.to;
  return (
    <section className="flex h-33 shrink-0 items-stretch gap-5 rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex w-50 shrink-0 flex-col gap-1">
        <button type="button" onClick={onToggle} className="flex items-baseline gap-1.5 text-left">
          <b className="text-sm font-semibold">{t(LABEL[series])}</b>
          <span className="text-xs text-muted-foreground">{t("rep.band.collapse")}</span>
        </button>
        <span className={cn("font-mono text-2xl font-semibold tracking-tight", tone)}>{fmt(total, true)}</span>
        <span className="text-xs text-muted-foreground">
          {t("rep.band.roi")} <span className={cn((totals?.roi ?? 0) > 0 ? "text-ok" : (totals?.roi ?? 0) < 0 ? "text-destructive" : "")}>{formatPct(totals?.roi, { digits: 0, signed: true })}</span>
          {prevTotals && total !== undefined && val(prevTotals) !== undefined ? (
            <>
              {" · "}
              <span className={(total - val(prevTotals)!) >= 0 ? "text-ok" : "text-destructive"}>{series === "cr" ? formatPp(total - val(prevTotals)!) : fmt(total - val(prevTotals)!, true)}</span> {t("rep.band.vsPrev", { n: daysIn({ from: p.from, to: p.to }) })}
            </>
          ) : null}
        </span>
        <span className="mt-auto flex flex-col gap-0.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="size-2 rounded-xs bg-ok" />
            {t("rep.band.this")}
          </span>
          {engine.compare ? (
            <span className="flex items-center gap-1.5">
              <span aria-hidden className="w-2.5 border-t border-dashed border-muted-foreground" />
              {t("rep.band.prev", { n: daysIn(engine.compare) })}
            </span>
          ) : null}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 items-end gap-2.5">
        {days.length === 0 ? (
          <span className="pb-6 text-xs text-muted-foreground">{DASH}</span>
        ) : (
          days.map((d) => {
            const v = val(d.v) ?? 0;
            const pv = val(d.prev);
            const live = d.day === today;
            const ink = v < 0 ? "destructive" : "ok";
            return (
              <div key={d.day} className="flex min-w-0 flex-1 flex-col items-center gap-0.75">
                <span className="font-mono text-xs">{fmt(v, series === "profit")}</span>
                <span className="relative block h-15 w-full max-w-24">
                  <span
                    className="absolute inset-x-0 bottom-0 rounded-t-xs"
                    style={{
                      height: Math.max(2, (Math.abs(v) / max) * 60),
                      background: live
                        ? `repeating-linear-gradient(135deg, color-mix(in oklab, var(--${ink}) 60%, transparent) 0 4px, color-mix(in oklab, var(--${ink}) 25%, transparent) 4px 8px)`
                        : `color-mix(in oklab, var(--${ink}) 80%, transparent)`,
                    }}
                  />
                  {pv !== undefined ? (
                    <span aria-hidden title={t("rep.prevValue", { value: fmt(pv, series === "profit") })} className="absolute -inset-x-1 border-t border-dashed border-muted-foreground" style={{ bottom: (Math.abs(pv) / max) * 60 }} />
                  ) : null}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  {d.day.slice(8, 10)}.{d.day.slice(5, 7)}
                  {live ? ` · ${t("rep.band.running")}` : ""}
                </span>
              </div>
            );
          })
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end justify-between">
        <SegmentedControl size="sm" aria-label={t("rep.band.series")} value={series} onValueChange={setSeries} options={SERIES.map((s) => ({ value: s, label: t(`rep.series.short.${s}` as TKey) }))} />
      </div>
    </section>
  );
}
